import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, Search, Menu, X } from "lucide-react";
import { useStore } from "@/contexts/StoreContext";

const NAV_LINKS = [
    { to: "/shop/tops", label: "Tops" },
    { to: "/shop/bottoms", label: "Bottoms" },
    { to: "/shipping", label: "Shipping & Returns" },
    { to: "/lookbook", label: "Lookbook" },
    { to: "/stockists", label: "Locations" },
];

const Navbar = () => {
    const { cartCount, setCartOpen, wishlist } = useStore();
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const openSearch = () => {
        const params = new URLSearchParams(location.search);
        params.set("focusSearch", "1");
        navigate({
            pathname: "/shop",
            search: `?${params.toString()}`,
        });
    };

    return (
        <header
            className="sticky top-0 z-40 bg-background border-b-4 border-black"
            data-testid="navbar"
        >
            <div className="px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
                <Link
                    to="/"
                    className="flex items-center gap-3 font-heading text-2xl md:text-3xl tracking-tight"
                    data-testid="logo-link"
                >
                    <span>
                        UKNITWEAR<span className="text-hazard">.</span>
                    </span>
                    <img
                        src={`${process.env.PUBLIC_URL}/navbar-logo.png`}
                        alt=""
                        aria-hidden="true"
                        className="h-9 w-9 -translate-y-1 md:h-10 md:w-10 object-contain"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            data-testid={`nav-link-${link.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `font-mono-ui text-xs uppercase tracking-[0.2em] hover:text-hazard transition-colors ${
                                    isActive ? "text-hazard" : "text-foreground"
                                }`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-1 md:gap-2">
                    <button
                        type="button"
                        aria-label="Search"
                        className="hidden p-2 transition-colors hover:bg-black hover:text-white md:block"
                        data-testid="search-button"
                        onClick={openSearch}
                    >
                        <Search className="w-5 h-5" />
                    </button>
                    <Link
                        to="/wishlist"
                        className="relative p-2 transition-colors hover:bg-black hover:text-white"
                        data-testid="nav-wishlist"
                        aria-label="Wishlist"
                    >
                        <Heart className="w-5 h-5" />
                        {wishlist.length > 0 && (
                            <span
                                className="absolute -top-0.5 -right-0.5 bg-hazard text-white text-[10px] font-mono-ui w-4 h-4 flex items-center justify-center"
                                data-testid="wishlist-count-badge"
                            >
                                {wishlist.length}
                            </span>
                        )}
                    </Link>
                    <button
                        type="button"
                        aria-label="Cart"
                        onClick={() => setCartOpen(true)}
                        className="relative p-2 transition-colors hover:bg-black hover:text-white"
                        data-testid="nav-cart-button"
                    >
                        <ShoppingBag className="w-5 h-5" />
                        {cartCount > 0 && (
                            <span
                                className="absolute -top-0.5 -right-0.5 bg-hazard text-white text-[10px] font-mono-ui w-4 h-4 flex items-center justify-center"
                                data-testid="cart-count-badge"
                            >
                                {cartCount}
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        aria-label="Menu"
                        aria-expanded={mobileOpen}
                        className="p-2 transition-colors hover:bg-black hover:text-white lg:hidden"
                        data-testid="nav-menu-toggle"
                        onClick={() => setMobileOpen((o) => !o)}
                    >
                        {mobileOpen ? (
                            <X className="w-5 h-5" />
                        ) : (
                            <Menu className="w-5 h-5" />
                        )}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div
                    className="border-t-2 border-black bg-background lg:hidden"
                    data-testid="nav-menu"
                >
                    <nav className="flex flex-col items-start">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                onClick={() => setMobileOpen(false)}
                                data-testid={`nav-menu-${link.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `w-full px-4 py-4 border-b border-black/20 font-mono-ui text-sm uppercase tracking-[0.2em] transition-colors hover:bg-black hover:text-white ${
                                        isActive ? "text-hazard" : "text-foreground"
                                    }`
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Navbar;
